// Ultimate Card Game (UCG) - Bot AI doctrines (hidden play styles)
//
// A doctrine gives a match its character: which cards the bot's deck leans
// on, which it plays first, how it paces its waves and superweapon. It is
// rolled per match and never shown anywhere (design decision E2 in
// docs/AI_OPPONENT_IMPLEMENTATION_PLAN_2026-09-22.md) - players can only
// read it from the bot's behaviour.
//
// Doctrines are orthogonal to difficulty (js/engine/ai/profiles.js): they
// flavour a tier, never replace it. That is why the wave knobs only scale
// an existing wave config (Easy/Normal stream and stay streaming) and are
// clamped to the measured threshold corridor - outside it waves measurably
// lose strength, and the difficulty ladder would shift.
//
// Cards are matched by tags derived from their stats (cardTags()), not by
// name, so a newly enabled card joins every doctrine automatically.
//
// Doctrine fields:
//   races        base races (deck race) the doctrine is rolled for
//   pickWeight   relative chance in the random roll
//   deckBias     { tag: factor } - card weight = product of its tags'
//                factors; the deck holds round(4 x weight) copies (1..12),
//                so a neutral card keeps 4 and a disliked one drops below.
//                Copies are draw chances (flat draws): this reshapes the
//                rarity mix too, see buildDoctrineDeck()
//   playBias     { tag: factor } - nudges the hand scorer toward tagged
//                cards (+15 per unit above 1)
//   waves        { thresholdMul, defendFactorMul } on top of the tier's
//                wave config, if the tier has one
//   nuke         { thetaOffset } added to the tier's firing threshold

import { getActiveCards } from './cards.js';

export const WAVE_THRESHOLD_CORRIDOR = Object.freeze({ min: 30, max: 40 });
// 4 neutral copies (not 2) so weights between 1 and 2 still resolve to
// distinct copy counts - with 2, factors 1.4 and 1.6 both rounded to 3.
const NEUTRAL_COPIES = 4;
const MAX_COPIES = 12;
const PLAY_BIAS_POINTS = 15;

export const AI_DOCTRINES = Object.freeze({
  // Neutral reference: the full race pool, unweighted.
  standard: {
    races: ['Human', 'Sul'],
    pickWeight: 1,
    deckBias: {},
    playBias: {},
    waves: { thresholdMul: 1, defendFactorMul: 1 },
    nuke: { thetaOffset: 0 }
  },
  // Many cheap bodies, early pressure, trigger-happy superweapon. (Not
  // "blitz" - that is the name of an online game mode.) All Sul Commons are
  // cheap already, so `light` carries the lean into the Uncommon cards too.
  rush: {
    races: ['Human', 'Sul'],
    pickWeight: 1,
    deckBias: { cheap: 2, light: 1.25, heavy: 0.4, structure: 0.5 },
    playBias: { cheap: 1.3 },
    waves: { thresholdMul: 1, defendFactorMul: 1.25 },
    nuke: { thetaOffset: -10 }
  },
  // Structures and range, patient superweapon. (Bigger waves and earlier
  // defending measured 43% vs standard on Hard; without them 46%.)
  bastion: {
    races: ['Human', 'Sul'],
    pickWeight: 1,
    deckBias: { structure: 3, longRange: 1.5, air: 0.6 },
    playBias: { structure: 1.4 },
    waves: { thresholdMul: 1, defendFactorMul: 1 },
    nuke: { thetaOffset: 10 }
  },
  // Air superiority. (Bigger waves measured 44% vs standard on Hard;
  // without them 48%.)
  airWing: {
    races: ['Human', 'Sul'],
    pickWeight: 1,
    deckBias: { air: 3, antiAir: 1.5 },
    playBias: { air: 1.3 },
    waves: { thresholdMul: 1, defendFactorMul: 1 },
    nuke: { thetaOffset: 0 }
  },
  // Long-range and splash behind a front, bigger waves.
  artillery: {
    races: ['Human', 'Sul'],
    pickWeight: 1,
    deckBias: { longRange: 2.5, splash: 2 },
    playBias: { longRange: 1.3 },
    waves: { thresholdMul: 1.25, defendFactorMul: 1 },
    nuke: { thetaOffset: 0 }
  }
  // No Sul-only "hive" (Symbiosis hunting): three Loafs fusing into one
  // random Pillar trades the race's most cost-efficient cards for a weaker
  // one, so hunting it lost 23-25% vs standard; softened enough to be fair
  // it was just rush again. See the plan, "Stand nach Phase 3".
});

// Stat-derived tags a doctrine can weight. Pure function of the card data.
export function cardTags(card) {
  const tags = new Set();
  if (card.isSpell || card.type === 'Spell') {
    tags.add('spell');
    return tags;
  }
  tags.add(card.isAir ? 'air' : 'ground');
  const armed = (card.damage || 0) > 0 || !!card.hasDualTether || !!card.hasBeamTether;
  if (armed && card.targetsAir !== false) tags.add('antiAir');
  if (card.isStructure || card.type === 'Structure' || card.isModule || card.type === 'Module') tags.add('structure');
  if ((card.range || 0) >= 10) tags.add('longRange');
  if ((card.cost || 0) <= 7) tags.add('cheap');
  if ((card.cost || 0) <= 12) tags.add('light');
  if ((card.cost || 0) >= 30) tags.add('heavy');
  if ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0) tags.add('splash');
  if (card.isCloaked || card.name === 'Anti Shade' || card.unitType === 'AntiShade') tags.add('cloak');
  return tags;
}

// Product of a bias map's factors over a card's tags (1 = neutral).
export function biasFactor(bias, card) {
  let factor = 1;
  if (!bias) return factor;
  for (const tag of cardTags(card)) {
    if (bias[tag] !== undefined) factor *= bias[tag];
  }
  return factor;
}

export function doctrinesForRace(race) {
  return Object.keys(AI_DOCTRINES).filter(id => AI_DOCTRINES[id].races.includes(race));
}

// Weighted roll among the doctrines valid for `race`, from the given rng
// (the bot's sim rng - deterministic in seeded sims).
export function pickDoctrineId(race, rng) {
  const ids = doctrinesForRace(race);
  if (ids.length === 0) return 'standard';
  const total = ids.reduce((sum, id) => sum + AI_DOCTRINES[id].pickWeight, 0);
  let roll = rng.next() * total;
  for (const id of ids) {
    roll -= AI_DOCTRINES[id].pickWeight;
    if (roll < 0) return id;
  }
  return ids[ids.length - 1];
}

// Deck for CardSystem.setDeck(): the race's active roster (the same pool
// DECKS_DATA builds from), each card repeated by its doctrine weight.
// Every copy is a draw-pool entry, so a doctrine moves ALL draw chances, the
// Epic share included (flat draws, the default: BALANCE.flatDrawRates; e.g.
// Human Epic 35% standard, 19% rush, 42% bastion). Only the legacy rarity
// mode (?rarityDraw=1, BALANCE.rarityWeights) rolls the tier first and keeps
// the Epic odds fixed. Only Easy and Normal deal this deck - Hard and up
// play a procedural archetype deck (js/data/aiDecks.js). See
// docs/AI_MASTERPLAN_2026-10.md, B17.
export function buildDoctrineDeck(doctrineId, race) {
  return buildDeckFor(AI_DOCTRINES[doctrineId] || AI_DOCTRINES.standard, race);
}

// Same, for a doctrine definition object (calibration overrides in
// tools/ai-eval/ build one that is not in AI_DOCTRINES).
export function buildDeckFor(doctrine, race) {
  const cards = [];
  for (const card of getActiveCards()) {
    if (card.race !== race) continue;
    const weight = biasFactor(doctrine.deckBias, card);
    let copies = Math.max(1, Math.min(MAX_COPIES, Math.round(NEUTRAL_COPIES * weight)));
    // Base modules can only be built once; Claiming Pillar is a unique territory anchor (max 1 copy)
    if (card.type === 'Module' || card.isModule || card.name === 'Claiming Pillar') {
      copies = 1;
    }
    // Anti Shade: strict role constraint (cost 47, cannot attack buildings, cap at 2 copies to prevent handlocks)
    if (card.name === 'Anti Shade' || card.cannotAttackBuildings) {
      copies = Math.min(2, copies);
    }
    for (let i = 0; i < copies; i++) cards.push(card.name);
  }

  // Enforce max 1/3 deck cap for Anti Shade and Claiming Pillar
  const oneThirdCap = Math.max(1, Math.floor(cards.length / 3));
  for (const restricted of ['Anti Shade', 'Claiming Pillar']) {
    let count = cards.filter(n => n === restricted).length;
    while (count > oneThirdCap) {
      const idx = cards.lastIndexOf(restricted);
      if (idx !== -1) {
        cards.splice(idx, 1);
        count--;
      } else break;
    }
  }

  return { race, cards };
}

// Hand-scorer nudge for a card under a doctrine.
export function playBiasPoints(doctrine, card) {
  return (biasFactor(doctrine?.playBias, card) - 1) * PLAY_BIAS_POINTS;
}
